<template>
  <div class="mall-h5">
    <!-- 顶部导航 -->
    <div class="mh5-topbar">
      <van-icon name="arrow-left" @click="goBack" />
      <span>订单详情</span>
    </div>

    <div v-if="loading && !order" class="mh5-loading">
      <van-loading type="spinner" size="24px">加载中...</van-loading>
    </div>

    <template v-else-if="order">
      <!-- 状态头 -->
      <div :class="['mh5-status', statusClass(order.orderStatus)]">
        <div class="mh5-status__name">{{ statusName(order.orderStatus) }}</div>
        <div class="mh5-status__no">订单号：{{ order.orderNo }}</div>
        <div class="mh5-status__time" v-if="order.createTime">下单时间：{{ formatTime(order.createTime) }}</div>
      </div>

      <!-- 收货信息 -->
      <div class="mh5-section" v-if="order.receiverName || order.receiverPhone || formatAddress(order)">
        <div class="mh5-section__head">
          <van-icon name="location-o" />
          <span>收货信息</span>
        </div>
        <div class="mh5-receiver">
          <div class="mh5-receiver__line" v-if="order.receiverName || order.receiverPhone">
            <span class="mh5-receiver__name">{{ order.receiverName }}</span>
            <span class="mh5-receiver__phone">{{ order.receiverPhone }}</span>
          </div>
          <div class="mh5-receiver__addr" v-if="formatAddress(order)">{{ formatAddress(order) }}</div>
        </div>
      </div>

      <!-- 物流信息 -->
      <div class="mh5-section" v-if="showExpressSection">
        <div class="mh5-section__head">
          <van-icon name="logistics-o" />
          <span>{{ isPickup ? '提货方式' : '物流信息' }}</span>
        </div>
        <!-- 线下自提 -->
        <div class="mh5-express mh5-express--pickup" v-if="isPickup">
          <van-icon name="shop-o" class="mh5-express__pickup-icon" />
          <div class="mh5-express__pickup-text">线下自提</div>
          <div class="mh5-express__pickup-tip">请到店自提，无需快递配送</div>
        </div>
        <!-- 普通快递-已发货 -->
        <div class="mh5-express" v-else-if="order.expressNo">
          <div class="mh5-express__row">
            <span class="mh5-express__label">快递公司</span>
            <span class="mh5-express__value">{{ order.expressCompany || '快递' }}</span>
          </div>
          <div class="mh5-express__row">
            <span class="mh5-express__label">快递单号</span>
            <span class="mh5-express__value">{{ order.expressNo }}</span>
            <span class="mh5-express__copy" @click="copyText(order.expressNo)">复制</span>
          </div>
        </div>
        <!-- 普通快递-未发货占位 -->
        <div class="mh5-express-empty" v-else>
          <van-icon name="logistics-o" class="mh5-express-empty__icon" />
          <div class="mh5-express-empty__text">暂未发货</div>
          <div class="mh5-express-empty__tip">商家发货后将在此更新物流信息</div>
        </div>
      </div>

      <!-- 商品清单 -->
      <div class="mh5-section" v-if="(order.orderItems || []).length">
        <div class="mh5-section__head">
          <van-icon name="goods" />
          <span>商品清单</span>
          <span class="mh5-section__count">共 {{ itemCount }} 件</span>
        </div>
        <div class="mh5-goods-list">
          <div class="mh5-goods" v-for="it in (order.orderItems || [])" :key="it.id">
            <div class="mh5-goods__img">
              <img v-if="it.productImg" :src="it.productImg" />
              <div v-else class="mh5-noimg"><van-icon name="photo-o" /></div>
            </div>
            <div class="mh5-goods__body">
              <div class="mh5-goods__name">{{ it.productName }}</div>
              <div class="mh5-goods__spec" v-if="it.spec">{{ it.spec }}</div>
              <div class="mh5-goods__bottom">
                <span class="mh5-price">¥{{ it.price }}</span>
                <span class="mh5-goods__qty">×{{ it.quantity }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 订单信息 -->
      <div class="mh5-section">
        <div class="mh5-section__head">
          <van-icon name="orders-o" />
          <span>订单信息</span>
        </div>
        <div class="mh5-info">
          <div class="mh5-info__row">
            <span class="mh5-info__label">订单号</span>
            <span class="mh5-info__value">{{ order.orderNo }}</span>
          </div>
          <div class="mh5-info__row" v-if="order.createTime">
            <span class="mh5-info__label">下单时间</span>
            <span class="mh5-info__value">{{ formatTime(order.createTime) }}</span>
          </div>
          <div class="mh5-info__row" v-if="order.remark">
            <span class="mh5-info__label">订单备注</span>
            <span class="mh5-info__value">{{ order.remark }}</span>
          </div>
          <div class="mh5-info__row mh5-info__row--total">
            <span class="mh5-info__label">订单金额</span>
            <span class="mh5-info__value mh5-price">¥{{ order.totalAmount }}</span>
          </div>
        </div>
      </div>
    </template>

    <!-- 空状态 -->
    <div v-else class="mh5-empty">
      <van-empty image="error" description="订单不存在或加载失败" />
      <div class="mh5-empty__btn" @click="goBack">返回</div>
    </div>

    <div class="mh5-safe-bottom" />
  </div>
</template>

<script>
import { mallH5OrderDetail } from '@/api/mall'

// 线下自提标识,与后端 express_company 约定一致
const SELF_PICKUP = '线下自提'

const STATUS_MAP = {
  0: { name: '待支付', cls: 'is-wait' },
  1: { name: '已支付', cls: 'is-paid' },
  2: { name: '已发货', cls: 'is-ship' },
  3: { name: '已退单', cls: 'is-refund' },
  4: { name: '已取消', cls: 'is-cancel' },
  5: { name: '待确认', cls: 'is-confirm' }
}

export default {
  name: 'MallH5OrderDetail',
  data() {
    return {
      orderNo: '',
      order: null,
      loading: false
    }
  },
  computed: {
    itemCount() {
      return (this.order && this.order.orderItems || []).reduce((n, it) => n + (it.quantity || 0), 0)
    },
    // 已支付 / 已发货 才展示物流区块；无快递单号时展示「暂未发货」占位
    showExpressSection() {
      const s = Number(this.order && this.order.orderStatus)
      return s === 1 || s === 2
    },
    // 线下自提:express_company 为「线下自提」
    isPickup() {
      return !!(this.order && this.order.expressCompany === SELF_PICKUP)
    }
  },
  created() {
    this.orderNo = this.$route.query.orderNo
    if (!this.orderNo) {
      this.$toast('缺少订单号')
      return
    }
    this.fetchDetail()
  },
  methods: {
    statusName(s) { return (STATUS_MAP[s] || {}).name || '未知' },
    statusClass(s) { return (STATUS_MAP[s] || {}).cls || '' },
    formatTime(t) {
      if (!t) return ''
      return String(t).replace('T', ' ').substring(0, 16)
    },
    formatAddress(o) {
      if (!o) return ''
      return [o.receiverProvince, o.receiverCity, o.receiverDistrict, o.receiverAddress].filter(Boolean).join('')
    },
    async fetchDetail() {
      // 先用列表页缓存的订单(含商品明细)兜底,避免详情接口未返回 orderItems 时商品不显示
      try {
        const preview = JSON.parse(sessionStorage.getItem('mall_order_preview') || 'null')
        if (preview && preview.orderNo === this.orderNo) this.order = preview
      } catch (e) { /* ignore */ }

      this.loading = true
      try {
        const res = await mallH5OrderDetail(this.orderNo)
        const d = (res && res.data) || {}
        this.order = {
          ...(this.order || {}),
          ...d,
          orderItems: (d.orderItems && d.orderItems.length) ? d.orderItems : ((this.order && this.order.orderItems) || [])
        }
      } catch (e) {
        if (!this.order) this.$toast('获取订单详情失败')
      } finally {
        this.loading = false
      }
    },
    copyText(text) {
      if (!text) return
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
        this.$toast('已复制')
      } catch (e) {
        this.$toast('复制失败')
      }
      document.body.removeChild(ta)
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace({ path: '/h5/mall/inquiry' })
      }
    }
  }
}
</script>

<style scoped>
/* ===== 全局基调 ===== */
.mall-h5 {
  min-height: 100vh;
  background: #F8F4F0;
  padding-bottom: 30px;
  -webkit-font-smoothing: antialiased;
}

/* ===== 顶部导航 ===== */
.mh5-topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  background: #fff;
  font-size: 17px;
  font-weight: 600;
  color: #3C3C3C;
  padding: 0 14px;
  box-shadow: 0 1px 0 rgba(0,0,0,0.04);
}
.mh5-topbar i {
  position: absolute;
  left: 14px;
  font-size: 20px;
  color: #666;
  cursor: pointer;
}

/* ===== 加载/空状态 ===== */
.mh5-loading { text-align: center; padding: 60px 0; }
.mh5-empty { padding-top: 40px; }
.mh5-empty__btn {
  margin: 12px auto 0;
  width: 120px;
  height: 40px;
  line-height: 40px;
  text-align: center;
  background: linear-gradient(135deg, #C0392B, #A3312A);
  color: #fff;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
}

/* ===== 状态头 ===== */
.mh5-status {
  margin: 12px 14px;
  padding: 18px 16px;
  border-radius: 14px;
  color: #fff;
  background: linear-gradient(135deg, #C0392B, #A3312A);
  box-shadow: 0 6px 18px rgba(192,57,43,0.22);
}
.mh5-status.is-wait { background: linear-gradient(135deg, #E6A23C, #CF8A2E); box-shadow: 0 6px 18px rgba(230,162,60,0.22); }
.mh5-status.is-paid { background: linear-gradient(135deg, #C0392B, #A3312A); }
.mh5-status.is-ship { background: linear-gradient(135deg, #C9A96E, #B8944E); box-shadow: 0 6px 18px rgba(201,169,110,0.22); }
.mh5-status.is-confirm { background: linear-gradient(135deg, #409eff, #337ecc); box-shadow: 0 6px 18px rgba(64,158,255,0.22); }
.mh5-status.is-refund,
.mh5-status.is-cancel { background: linear-gradient(135deg, #909399, #777); box-shadow: 0 6px 18px rgba(144,147,153,0.22); }
.mh5-status__name { font-size: 20px; font-weight: 700; }
.mh5-status__no { font-size: 13px; margin-top: 8px; opacity: 0.92; }
.mh5-status__time { font-size: 12px; margin-top: 4px; opacity: 0.82; }

/* ===== 卡片区块 ===== */
.mh5-section {
  background: #fff;
  margin: 12px 14px;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
}
.mh5-section__head {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #3C3C3C;
  margin-bottom: 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #F0ECE6;
}
.mh5-section__head i { font-size: 18px; color: #C0392B; margin-right: 8px; }
.mh5-section__count { margin-left: auto; font-size: 13px; font-weight: 400; color: #B0A89E; }

/* ===== 收货信息 ===== */
.mh5-receiver__line { display: flex; align-items: center; gap: 12px; }
.mh5-receiver__name { font-size: 15px; font-weight: 600; color: #3C3C3C; }
.mh5-receiver__phone { font-size: 13px; color: #666; }
.mh5-receiver__addr { font-size: 13px; color: #666; line-height: 1.6; margin-top: 6px; }

/* ===== 物流信息 ===== */
.mh5-express__row {
  display: flex;
  align-items: center;
  padding: 7px 0;
}
.mh5-express__label { width: 76px; font-size: 13px; color: #B0A89E; flex-shrink: 0; }
.mh5-express__value { font-size: 14px; color: #3C3C3C; flex: 1; word-break: break-all; }
.mh5-express__copy {
  font-size: 12px;
  color: #C0392B;
  padding: 2px 10px;
  border: 1px solid #C0392B;
  border-radius: 10px;
  margin-left: 8px;
  cursor: pointer;
}

/* 线下自提 */
.mh5-express--pickup {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 18px 0 14px;
  text-align: center;
}
.mh5-express__pickup-icon {
  font-size: 34px;
  color: #C9A96E;
}
.mh5-express__pickup-text {
  margin-top: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #8C7A5A;
}
.mh5-express__pickup-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #B0A89E;
}

/* ===== 物流信息-未发货占位 ===== */
.mh5-express-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 18px 0 14px;
  text-align: center;
}
.mh5-express-empty__icon {
  font-size: 34px;
  color: #D8CFC4;
}
.mh5-express-empty__text {
  margin-top: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #8C8580;
}
.mh5-express-empty__tip {
  margin-top: 4px;
  font-size: 12px;
  color: #B0A89E;
}

/* ===== 商品清单 ===== */
.mh5-goods {
  display: flex;
  padding: 10px 0;
}
.mh5-goods + .mh5-goods { border-top: 1px solid #F8F4F0; }
.mh5-goods__img {
  width: 64px;
  height: 64px;
  border-radius: 10px;
  overflow: hidden;
  background: #FAF8F6;
  flex-shrink: 0;
  padding: 4px;
  box-sizing: border-box;
}
.mh5-goods__img img { width: 100%; height: 100%; object-fit: contain; }
.mh5-noimg {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #DFD8CF;
  font-size: 22px;
}
.mh5-goods__body { flex: 1; margin-left: 12px; display: flex; flex-direction: column; justify-content: center; }
.mh5-goods__name { font-size: 14px; color: #3C3C3C; line-height: 1.4; font-weight: 500; }
.mh5-goods__spec { font-size: 12px; color: #B0A89E; margin-top: 2px; }
.mh5-goods__bottom { display: flex; justify-content: space-between; align-items: center; margin-top: 6px; }
.mh5-goods__qty { font-size: 13px; color: #B0A89E; }
.mh5-price { color: #C0392B; font-weight: 600; font-size: 15px; }

/* ===== 订单信息 ===== */
.mh5-info__row {
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
}
.mh5-info__label { width: 76px; font-size: 13px; color: #B0A89E; flex-shrink: 0; }
.mh5-info__value { font-size: 14px; color: #3C3C3C; flex: 1; word-break: break-all; }
.mh5-info__row--total { border-top: 1px solid #F0ECE6; margin-top: 4px; padding-top: 12px; }
.mh5-info__row--total .mh5-info__value { font-size: 17px; font-weight: 700; }

.mh5-safe-bottom { height: 12px; }
</style>
